import { SizableText, YStack, useMedia, Unspaced } from '@mythical/ui'
import usePagination from 'app/hooks/use-pagination'
import {
  Comment as CommentType,
  createCommentsQuery,
  filterComment,
  mapToComments,
} from 'app/queries/comments/comments'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'

import { Button } from '../primitive/Button'
import { BlockModal } from './BlockModal'
import { Comment } from './Comment'
import { CommentForm } from './CommentForm'
import { DeleteModal } from './DeleteModal'
import { ReportModal } from './ReportModal'

const parentQuery = createCommentsQuery()

export type CommentAction = 'delete' | 'report' | 'block'
export type HandleCommentAction = (
  action: CommentAction,
  item: CommentType
) => void
interface CommentsListProps {
  parent: string
  isReplies?: boolean
}

const options = {
  limit: 10,
  formatFn: mapToComments,
}

export const CommentsList = observer(
  ({ parent, isReplies }: CommentsListProps) => {
    const memoedParams = useMemo(() => {
      return { parent }
    }, [parent])

    const { userStore } = useStores()
    const loggedIn = !!userStore?.user?.id

    const [deleteModal, setDeleteModal] = useState(false)
    const [reportModal, setReportModal] = useState(false)
    const [blockModal, setBlockModal] = useState(false)
    const [actionedItem, setActionedItem] = useState<CommentType>(
      {} as CommentType
    )

    const { isFetchingNextPage, data, error, hasMore, fetchData, fetchMore } =
      usePagination(parentQuery, memoedParams, options)
    const media = useMedia()

    const docs = (data?.docs || []).filter(filterComment)

    const handleCommentAction: HandleCommentAction = (action, item) => {
      switch (action) {
        case 'delete':
          setActionedItem(item)
          setDeleteModal(true)
          break
        case 'report':
          setActionedItem(item)
          setReportModal(true)
          break
        case 'block':
          setActionedItem(item)
          setBlockModal(true)
          break
      }
    }

    const onUpdate = async () => {
      await fetchData()
    }

    return (
      <YStack
        id="comments"
        width="100%"
        space="$3"
        {...(media.gtXs && {
          space: '$4',
        })}
      >
        <YStack space="$2">
          {!isReplies && loggedIn && (
            <CommentForm parent={parent} onSubmitted={fetchData} />
          )}
          {error && (
            <SizableText color="$primary1">{error.message}</SizableText>
          )}

          {docs.map((item: CommentType) => (
            <Comment
              item={item}
              key={item.id}
              level={1}
              handleAction={handleCommentAction}
              onUpdate={onUpdate}
            />
          ))}
          {hasMore && docs.length !== 0 && (
            <YStack jc="center" ai="center" pt="$2">
              <Button
                justifyContent="center"
                onPress={() => fetchMore()}
                loading={isFetchingNextPage}
              >
                Load More
              </Button>
            </YStack>
          )}

          <Unspaced>
            {deleteModal && (
              <DeleteModal
                modalOpen={deleteModal}
                setModalOpen={setDeleteModal}
                id={actionedItem.id}
                onDelete={() => {
                  setDeleteModal(false)
                  onUpdate()
                }}
              />
            )}

            {blockModal && (
              <BlockModal
                modalOpen={blockModal}
                setModalOpen={setBlockModal}
                id={actionedItem.memberId}
                onBlock={() => {
                  setBlockModal(false)
                  onUpdate()
                }}
              />
            )}

            {reportModal && (
              <ReportModal
                modalOpen={reportModal}
                setModalOpen={setReportModal}
                id={actionedItem.id}
                onReport={() => {
                  setReportModal(false)
                  onUpdate()
                }}
              />
            )}
          </Unspaced>
        </YStack>
      </YStack>
    )
  }
)
